<script setup lang="ts">
import { inject, ref, watch } from "vue"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import { faHeart } from "@fal/faHeart";import { faShoppingCart } from "@fal/faShoppingCart";import { faSignOut } from "@fal/faSignOut";import { faUser } from "@fal/faUser";import { faSignIn } from "@fal/faSignIn";import { faUserPlus } from "@fal/faUserPlus";import { faCog } from "@fal/faCog";
import { faSpinnerThird } from "@fad/faSpinnerThird";
import { faEnvelopeCircleCheck } from '@fortawesome/free-solid-svg-icons'
import { faLaptopCode } from "@fas/faLaptopCode";import { faLayerGroup } from "@fas/faLayerGroup";
import { library } from "@fortawesome/fontawesome-svg-core"
import { getStyles } from "@/Composables/styles"
import { checkVisible, textReplaceVariables } from "@/Composables/Workshop"
import { urlLoginWithRedirect } from "@/Composables/urlLoginWithRedirect"
import Button from "@/Components/Elements/Buttons/Button.vue"
import LoadingIcon from "@/Components/Utils/LoadingIcon.vue"
import LinkIris from "@/Iris/Components/LinkIris.vue"
import GoldReward from "@/Components/Utils/GoldReward.vue"
import { useFormatTime } from "@/Composables/useFormatTime"
import { ctrans } from "@/Composables/useTrans"
import { useOpenBasketPanelOnClick } from "@/Iris/Composables/useSidePanel"
import { useIrisLogout } from "@/Iris/Composables/useIrisLogout"

library.add(faLaptopCode, faHeart, faShoppingCart, faSignOut, faUser, faSignIn, faUserPlus, faEnvelopeCircleCheck, faLayerGroup, faSpinnerThird, faCog)

const props = defineProps<{
    screenType?: "desktop" | "mobile" | "tablet"
}>()

const screenTypeInject = inject("screenType", "desktop")

interface ModelTopbar1 {
    profile: {
        text: string
        visible: string
    };
    favourite: {
        text: string
        visible: string
    };
    cart: {
        text: string
        visible: string
    };
    register: {
        text: string
        visible: string
    };
    logout: {
        text: string
        visible: string
    };
    login: {
        text: string
        visible: string
    };
    greeting: {
        text: string
    };
    main_title: {
        text: string
        visible: string
    };
    container: {
        properties: {
            color: {}
            background: {}
        }
    };
}

const model = defineModel<ModelTopbar1>()
const isLoggedIn = inject("isPreviewLoggedIn", false)
const layout = inject("layout", {})

// Section: Logout
const { isLoadingLogout, logout: onClickLogout } = useIrisLogout(layout)
const openBasketPanelOnClick = useOpenBasketPanelOnClick()

const buttonClass = ref(getStyles(model.value?.button?.container?.properties, screenTypeInject, false))
const buttonHoverClass = ref(getStyles(model.value?.button?.hover?.container?.properties, screenTypeInject,false))

watch(
  () => model.value?.button,
  () => {
    buttonClass.value = getStyles(model.value?.button?.container?.properties, screenTypeInject,false)
    buttonHoverClass.value = getStyles(model.value?.button?.hover?.container?.properties, screenTypeInject,false)
  },
  { deep: true }
)

const isLoadingBundle = ref(false)
const goToBundle = () => {
    isLoadingBundle.value = true
    const stored = localStorage.getItem('layout_dropshipping')
    const currentPlatform = stored ? JSON.parse(stored)?.currentPlatform : null
    const url = currentPlatform
        ? `/redirect-bundle-to-retina?platform=${encodeURIComponent(currentPlatform)}`
        : '/redirect-bundle-to-retina'
    window.location.href = url
}
</script>

<template>
    <div id="top_bar_1_iris" class="py-[10px] px-4 flex flex-col md:flex-row md:justify-between gap-x-4 md:sticky top-0 z-50"
        :style="{
        ...getStyles(layout?.app?.webpage_layout?.container?.properties, screenTypeInject),
        ...getStyles(model?.container?.properties, screenTypeInject),
        margin: 0,
    }">
        <!-- layout?.app?.webpage_layout?.container?.properties   // TODO: should exist in Retina -->

        <div class="flex-shrink flex flex-col md:flex-row items-center justify-between w-full ">
            <!-- Section: Main title -->
            <div v-if="layout.offer_data && isLoggedIn" class="text-center md:text-left md:flex md:gap-4">
                <span>
                    {{ ctrans("Hello") }}, 
                    <LinkIris href="/app/dashboard" :type="'internal'" class="inline-flex items-center justify-center hover:underline">
                        <span class="font-bold">{{ layout.iris_variables?.name }}</span>
                    </LinkIris>!
                </span>

                <div  v-if="layout.retina?.type == 'b2b'">
                    <!-- Section: GR Amnesty (all users have GR) -->
                    <span v-if="layout.offer_data.amnesty"
                        class="text-yellow-300 text-xs inline-flex items-center gap-x-1">
                        <span class="">{{ ctrans('Gold Reward Amnesty') }}</span>
                        <FontAwesomeIcon icon="fas fa-candle-holder" class="" fixed-width aria-hidden="true" />
                        <span class="text-xs">({{ ctrans('Until :amnestyUntil', {
                            amnestyUntil:
                                useFormatTime(layout.offer_data.amnesty_until, { formatTime: 'MMM do' }) }) }})</span>
                    </span>

                    <!-- Section: GR status -->
                    <span v-else-if="layout.offer_data?.type === 'gr'"
                        class="text-yellow-500 inline-flex items-center gap-x-1">
                        {{ layout.offer_data?.label }}
                        <GoldReward>
                            <template #default>
                                <div>
                                    <FontAwesomeIcon icon="fas fa-medal" class="text-yellow-500" fixed-width
                                        aria-hidden="true" />
                                    <div
                                        class="ml-1 inline-block align-middle w-20 text-xxs rounded-sm h-3 mt-1.5 bg-gray-200 relative overflow-hidden mb-2">
                                        <div class="absolute  left-0   top-0 h-full transition-all duration-1000 ease-in-out"
                                            :class="true ? 'xshimmer bg-green-500' : 'bg-green-500'" :style="{
                                                width: true ? layout.offer_data?.meter?.[0] / layout.offer_data?.meter?.[1] * 100 + '%' : '100%'
                                            }" />

                                        <div
                                            class="absolute inset-0 flex items-center justify-center text-xxs font-medium text-black">
                                            {{ Number(layout.offer_data?.meter?.[0]).toFixed(0) }} / {{
                                                Number(layout.offer_data?.meter?.[1]).toFixed(0) }} days
                                        </div>

                                    </div>
                                </div>
                            </template>
                        </GoldReward>
                    </span>

                    <span v-if="layout.offer_data?.type === 'fob'" class="text-yellow-500">
                        {{ layout.offer_data?.label }}
                        <FontAwesomeIcon icon="fas fa-sparkles" class="" fixed-width aria-hidden="true" />
                    </span>


                </div>
                
            </div>
            <div v-else-if="checkVisible(model?.main_title?.visible || null, isLoggedIn) && textReplaceVariables(model?.main_title?.text, layout.iris_variables)"
                class="text-center flex items-center"
                v-html="textReplaceVariables(model?.main_title?.text, layout.iris_variables)"
            />
            <!-- Reserve the greeting row while the first-hit data is still on the wire,
                 so the bar doesn't grow when "Hello, name" arrives -->
            <div v-else-if="layout.iris?.is_logged_in" class="invisible text-center md:text-left" aria-hidden="true">
                <span>{{ ctrans("Hello") }}!</span>
            </div>
        </div>

        <div class="hidden md:flex justify-between md:justify-start items-center gap-x-1 flex-wrap md:flex-nowrap">
            <!-- v-if="!layout.offer_data" -->
            <!-- Section: Profile -->
            <LinkIris  v-if="(checkVisible(model?.profile?.visible || null, isLoggedIn))" href="/app/dashboard" :type="'internal'" class="flex items-center justify-center" v-slot="{ isLoading } = { isLoading: false }">
                <Button
                 
                    :loading="isLoading"
                    icon="fal fa-user"
                    type="transparent"
                    class="button min-w-max"
                >
                    <template #icon>
                        <span v-tooltip="ctrans('My Account')">
                            <FontAwesomeIcon icon="fal fa-user" class="button" fixed-width aria-hidden="true" />
                        </span>
                    </template>
                    <template #label>
                        <!-- <span v-tooltip="ctrans('Profile')" class="button" v-html="textReplaceVariables(model?.profile?.text, layout.iris_variables)" /> -->
                        <!-- <span v-tooltip="ctrans('My Account')" class="button">{{ ctrans("My Account") }}</span> -->
                        <!-- <GoldReward v-if="layout.offer_data?.type === 'gr'" /> -->
                    </template>
                </Button>
            </LinkIris>

            <!-- Section: My Interest -->
            <LinkIris v-if="layout.retina?.type !== 'dropshipping' && checkVisible(model?.favourite?.visible || null, isLoggedIn)" href="/app/interest/favourites" :type="'internal'" v-slot="{ isLoading } = { isLoading: false }">
                <Button
                    v-tooltip="ctrans('My Interest')"
                    type="transparent"
                    :loading="isLoading"
                    icon="fal fa-heart"
                    class="button"
                >
                    <template #icon>
                        <FontAwesomeIcon icon="fal fa-heart" class="align-middle button" fixed-width aria-hidden="true" />
                    </template>
                     <template #label>
                        <!-- <div class="button whitespace-nowrap" v-if="model?.favourite?.text === `{{ favourites_count }}`"
                            v-html="textReplaceVariables(model?.favourite?.text, layout.iris_variables)" />
                        <div class="button" v-else-if="model?.favourite?.text === `{{ favourites_count }} favourites`">
                            {{ layout.iris_variables?.favourites_count }} {{ layout.iris_variables?.favourites_count > 1 ?
                            ctrans("favourites") : ctrans("favourite") }}
                        </div> -->
                    </template>
                </Button>
            </LinkIris>

            <LinkIris v-if="isLoggedIn" href="/app/dropshipping/back-in-stocks" :type="'internal'" v-slot="{ isLoading } = { isLoading: false }">
                <Button
                    v-tooltip="ctrans('Reminder back in stock')"
                    type="transparent"
                    :loading="isLoading"
                    class="button"
                >
                    <template #icon>
                        <FontAwesomeIcon icon="fas fa-envelope-circle-check" class="align-middle button" fixed-width aria-hidden="true" />
                    </template>
                    <template #label>
                        <span class="button">
                            {{ layout.iris_variables?.back_in_stock_count }}
                        </span>
                    </template>
                </Button>
            </LinkIris>
            <!-- section redirect to bundle -->
            <Button v-if="isLoggedIn && layout.retina?.type === 'dropshipping' &&  Object.keys(layout.user?.customerSalesChannels || {}).length > 0" v-tooltip="ctrans('Add Bundles')" type="transparent" class="button"
                @click="goToBundle" :loading="isLoadingBundle">
                <template #isLoadingBundle>
                    <span v-show="false" class="button"></span>
                </template>

                <template #label>
                    <FontAwesomeIcon icon="fas fa-layer-group" class="button" fixed-width />
                </template>
            </Button>

            <!-- Section: Basket (cart) -->
            <LinkIris v-if="(checkVisible(model?.cart?.visible || null, isLoggedIn) && layout.retina?.type == 'b2b')" href="/app/basket" :type="'internal'" v-slot="{ isLoading } = { isLoading: false }">
                <span class="contents" @click="openBasketPanelOnClick">
                <Button
                    
                    v-tooltip="ctrans('Cart count and amount')"  
                    :loading="isLoading"
                    type="transparent"
                    class="button min-w-max"
                >
                    <template #label="{ isLoadingVisit }">
                        <span v-tooltip="ctrans('Number of products line')" class="button -mr-1.5 whitespace-nowrap"
                            v-html="textReplaceVariables(`({{ cart_count }})`, layout.iris_variables)">
                        </span>
                        <LoadingIcon v-if="isLoadingVisit" />
                        <FontAwesomeIcon v-else icon="fal fa-shopping-cart" class="button" fixed-width
                            aria-hidden="true" />
                        <span class="button whitespace-nowrap" 
                            v-html="textReplaceVariables(`{{ cart_amount }} <span class='opacity-70'>({{ cart_products_amount }})</span>`, layout.iris_variables)">
                        </span>
                    </template>
                </Button>
                </span>
            </LinkIris>

            <!-- Section: Register -->
            <LinkIris  v-if="(checkVisible(model?.register?.visible || null, isLoggedIn))" href="/app/register" :type="'internal'" v-slot="{ isLoading } = { isLoading: false }">
                <Button
                   
                    :loading="isLoading"
                    icon="fal fa-user-plus"
                    type="transparent"
                    class="button"
                >
                    <template #icon>
                        <FontAwesomeIcon icon="fal fa-user-plus" fixed-width aria-hidden="true" class="button"/>
                    </template>
                    <template #label>
                        <span class="button whitespace-nowrap">
                            {{ ctrans("Register") }}
                        </span>
                    </template>
                </Button>
            </LinkIris>

            <!-- Section: Login -->
            <LinkIris   v-if="checkVisible(model?.login?.visible || null, isLoggedIn)" :href="urlLoginWithRedirect()" :type="'internal'" v-slot="{ isLoading } = { isLoading: false }">
                <Button
                    :loading="isLoading"
                    icon="fal fa-sign-in"
                    type="transparent"
                    class="button"
                >
                    <template #icon>
                        <FontAwesomeIcon icon="fal fa-sign-in" class="button" fixed-width aria-hidden="true" />
                    </template>
                    <template #label>
                        <span class="button whitespace-nowrap">
                            {{ ctrans("Login") }}
                        </span>
                    </template>
                </Button>
            </LinkIris>

            <!-- Section: Logout -->
            <Button
                v-if="checkVisible(model?.logout?.visible || null, isLoggedIn)"
                @click="() => onClickLogout()"
                icon="fal fa-sign-out"
                type="transparent"
                :loading="isLoadingLogout"
                class="button" 
            >
                <template #icon>
                    <FontAwesomeIcon icon="fal fa-sign-out" fixed-width aria-hidden="true" class="button" />
                </template>
                <template #label>
                    <span class="button whitespace-nowrap">
                        {{ ctrans("Logout") }}
                    </span>
                </template>
            </Button>
        </div>
    </div>
</template>


<style lang="scss" scoped>
.button {
  background: v-bind('buttonClass?.background || null') !important;
  color: v-bind('buttonClass?.color || null') !important;
  font-family: v-bind('buttonClass?.fontFamily || null') !important;
  font-size: v-bind('buttonClass?.fontSize || null') !important;
  font-style: v-bind('buttonClass?.fontStyle || null') !important;;

  &:hover {
    background: v-bind('buttonHoverClass?.background || null') !important;
    color: v-bind('buttonHoverClass?.color || null') !important;
    font-family: v-bind('buttonHoverClass?.fontFamily || null') !important;
    font-size: v-bind('buttonHoverClass?.fontSize || null') !important;
    font-style: v-bind('buttonHoverClass?.fontStyle || null') !important;;
  }
}
</style>